import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import apiUrl from "../../Apiurl";
import { resolveTranslationUsername, translationErrorMessage, translationErrorProduct } from "../../traduzioni/translationRequest";
import { loadTranslationQuota, translationAllowed } from "../../traduzioni/translationQuota";
import { TextSkeleton } from "../../interfaccia/loading";
import "./Traduzioni.css";

const CAMPI = [
  ["titolo", "Titolo"], ["descBreve", "Descrizione breve"],
  ["descLunga", "Descrizione lunga"], ["descIntro", "Testo intro"],
];
const LINGUE = { en: "Inglese", fr: "Francese", es: "Spagnolo" };
const STATI = {
  PRONTO: "Pronto", MANCANTE: "Mancante", PARZIALE: "Parziale", DA_AGGIORNARE: "Da aggiornare",
  NON_VERIFICATO: "Non verificato", ASSENTE: "Assente", SEGNAPOSTO: "Segnaposto", PRESENTE: "Presente",
  ALLINEATO: "Allineato", INVIATO_NON_VERIFICATO: "Inviato, non verificato", ERRORE_LETTURA: "Errore di lettura",
};

// Sezione di sola consultazione: italiano e traduzione affiancati, con HTML
// formattato. Le traduzioni si modificano dal tab "Descrizioni".
function TestoFormattato({ campo, testo, vuoto }) {
  if (!testo) return <p className="gray">{vuoto}</p>;
  if (campo === "titolo") return <p className="b">{testo}</p>;
  return <div className="traduzioniHtml" dangerouslySetInnerHTML={{ __html: testo }} />;
}

export default function Traduzioni({ prodotto, utente, salvato, tradPermessa, siti, refreshKey, onTranslated, onBusy, buttonClass }) {
  const { getAccessTokenSilently, user } = useAuth0();
  const [lingua, setLingua] = useState("en");
  const [stato, setStato] = useState(null);
  const [loading, setLoading] = useState(true);
  // Solo informativa: la quota la decide il server sui testi da tradurre davvero
  // (gli hit di cache non consumano), quindi non blocca il pulsante (#554).
  const [quotaEsaurita, setQuotaEsaurita] = useState(false);
  const [erroreStato, setErroreStato] = useState("");
  const [errore, setErrore] = useState("");
  // Errore dell'ultima traduzione: sopravvive al ricaricamento di stato e quota
  // che segue un salvataggio parziale.
  const [erroreTraduzione, setErroreTraduzione] = useState("");
  const [avviso, setAvviso] = useState("");
  const [busy, setBusy] = useState(false);
  const [conferma, setConferma] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const pending = useRef(false);
  const mounted = useRef(true);
  const canRead = Boolean(utente?.permessiAPI?.["read:database"]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setErroreTraduzione(""); setAvviso(""); }, [prodotto.codiceVS]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setStato(null);
    setErroreStato("");
    setErrore("");
    setQuotaEsaurita(false);
    if (!prodotto.codiceVS || !canRead) {
      setErroreStato("Stato e quota non disponibili: servono un codice VS e il permesso read:database.");
      setLoading(false);
      return () => controller.abort();
    }
    async function leggi() {
      const results = await Promise.allSettled([
        (async () => {
          const token = await getAccessTokenSilently();
          if (controller.signal.aborted) return;
          return axios.get(`${apiUrl}/prodotti/translate/stato/${encodeURIComponent(prodotto.codiceVS)}`,
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal });
        })(),
        loadTranslationQuota(getAccessTokenSilently, controller.signal),
      ]);
      if (controller.signal.aborted) return;
      const [copertura, quota] = results;
      if (copertura.status === "fulfilled" && Array.isArray(copertura.value?.data?.rows) &&
          Array.isArray(copertura.value?.data?.lingue) && Array.isArray(copertura.value?.data?.campi)) {
        setStato(copertura.value.data);
      } else {
        setErroreStato(copertura.status === "rejected" ? translationErrorMessage(copertura.reason) : "Stato traduzioni non disponibile.");
      }
      if (quota.status === "fulfilled") setQuotaEsaurita(!translationAllowed(quota.value.total, quota.value.limit));
      else setErrore(translationErrorMessage(quota.reason));
      setLoading(false);
    }
    leggi();
    return () => controller.abort();
  }, [prodotto.codiceVS, getAccessTokenSilently, canRead, refresh, refreshKey]);

  const lingue = stato?.lingue?.length ? stato.lingue : ["en", "fr"];
  const selezionata = lingue.includes(lingua) ? lingua : lingue[0];
  const motivo = busy ? "Traduzione in corso." : !salvato ? "Salva prima le modifiche: verranno tradotti i testi italiani salvati."
    : !prodotto._id ? "Salva il prodotto prima di tradurlo." : !utente?.permessiAPI?.["usage:googleapis"]
      ? "Serve il permesso usage:googleapis." : loading ? "Verifica della quota in corso." : "";
  const avvisoQuota = !motivo && (quotaEsaurita || tradPermessa === false)
    ? "Quota mensile esaurita o non configurata: si possono ottenere solo i testi già tradotti in cache, gli altri verranno rifiutati."
    : "";
  const campiConSorgente = CAMPI.filter(([campo]) => {
    const raw = String(prodotto.descrizioniSuPS?.[campo]?.it ?? "").trim();
    const presente = Boolean(raw.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim() || /<img\b/i.test(raw));
    return presente && stato?.campi.some((item) => item.campo === campo && item.siti.some((site) => siti.includes(site)));
  });

  async function traduci(modalita) {
    if (pending.current || motivo) return;
    pending.current = true;
    setBusy(true);
    onBusy(true);
    setErroreTraduzione("");
    setAvviso("");
    setConferma(false);
    const prima = CAMPI.map(([campo]) => prodotto.descrizioniSuPS?.[campo]?.[selezionata] ?? "");
    try {
      const token = await getAccessTokenSilently();
      const username = await resolveTranslationUsername({ username: utente?.username, email: user?.email, accessToken: token });
      const response = await axios.patch(`${apiUrl}/prodotti/translate/blindly`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { idDocProdotto: prodotto._id, username, lingue: selezionata, modalita },
      });
      if (mounted.current) {
        const dopo = CAMPI.map(([campo]) => response.data?.descrizioniSuPS?.[campo]?.[selezionata] ?? "");
        if (dopo.every((testo, index) => testo === prima[index])) {
          setAvviso(`Nessun campo da tradurre in ${LINGUE[selezionata] || selezionata}: i campi sono già tradotti, modificati a mano o senza testo italiano.`);
        }
        onTranslated(response.data);
        setRefresh((value) => value + 1);
      }
    } catch (error) {
      if (mounted.current) {
        setErroreTraduzione(translationErrorMessage(error));
        const parziale = translationErrorProduct(error);
        if (parziale) {
          onTranslated(parziale);
          setRefresh((value) => value + 1);
        }
      }
    } finally {
      pending.current = false;
      onBusy(false);
      if (mounted.current) setBusy(false);
    }
  }

  function statiCampo(campo) {
    const siti = stato?.campi?.find((item) => item.campo === campo)?.siti || [];
    return <>
      <span className="f6">{siti.length === 2 ? "VIG e BOT" : siti.length ? `solo ${siti[0]}` : "Siti non disponibili"}</span>
      {siti.map((fonte) => {
        const row = stato.rows.find((item) => item.fonte === fonte && item.lingua === selezionata);
        const mancanti = String(row?.campi_mancanti || "").split(",");
        return <div key={fonte} className="f6 mt1">{fonte}: Domino {row ? mancanti.includes(campo) ? "Mancante" : STATI[row.stato_domino] || row.stato_domino : "Non calcolato"}
          {" · "}Online {row ? STATI[row.stato_online] || row.stato_online : "Non verificato"}
          {Boolean(row?.copia_it) && " · Descrizioni con copia italiano"}</div>;
      })}
    </>;
  }

  return <section className="modificaProdottiPagina pa2" aria-label="Traduzioni prodotto" aria-busy={busy}>
    <div className="flex flex-wrap items-center mb3">
      <nav aria-label="Lingua traduzione" className="flex flex-wrap">
        {lingue.map((target) => <button key={target} type="button" className={`${buttonClass} mr2`}
          aria-pressed={selezionata === target} disabled={busy} onClick={() => { setLingua(target); setConferma(false); setAvviso(""); }}>
          {LINGUE[target] || target}</button>)}
      </nav>
      <button type="button" className={buttonClass} disabled={Boolean(motivo)} onClick={() => traduci("mancanti")}>Traduci</button>
      <button type="button" className={`${buttonClass} ml2`} disabled={Boolean(motivo) || !stato || !campiConSorgente.length}
        onClick={() => setConferma(true)}>Ritraduci tutti</button>
      <button type="button" className={`${buttonClass} ml2`} disabled={busy || loading}
        onClick={() => setRefresh((value) => value + 1)}>Aggiorna stato e quota</button>
    </div>
    {motivo && <p role="status">{motivo}</p>}
    {avvisoQuota && <p role="status">{avvisoQuota}</p>}
    {avviso && <p role="status">{avviso}</p>}
    {errore && <p role="alert">{errore}</p>}
    {erroreTraduzione && <p role="alert">{erroreTraduzione}</p>}
    {erroreStato && <p role="alert">{erroreStato}</p>}
    {loading && <TextSkeleton lines={2} />}
    {conferma && <div role="alertdialog" aria-label="Conferma ritraduzione" className="pa3 mb3 ba bordoColAlt">
      <p>Ritradurre in {LINGUE[selezionata]}? Questi campi verranno sovrascritti:</p>
      <ul>{campiConSorgente.map(([campo, label]) => <li key={campo}>{label}</li>)}</ul>
      <button type="button" className={buttonClass} disabled={Boolean(motivo)} onClick={() => traduci("tutti")}>Conferma ritraduzione</button>
      <button type="button" className={`${buttonClass} ml2`} onClick={() => setConferma(false)}>Annulla</button>
    </div>}
    <div className="traduzioniEditorHeading"><h3>Italiano</h3><h3>{LINGUE[selezionata]}</h3></div>
    {CAMPI.map(([campo, label]) => {
      const source = prodotto.descrizioniSuPS?.[campo]?.it || "";
      const target = prodotto.descrizioniSuPS?.[campo]?.[selezionata] || "";
      return <div key={campo} className="traduzioniEditorRow" data-campo={campo}>
        <div className="traduzioniEditorCell pa3 ba bordoColAlt">
          <h4 className="mt0">{label} · Italiano</h4>
          <TestoFormattato campo={campo} testo={source} vuoto="Nessun testo italiano." />
        </div>
        <div className="traduzioniEditorCell pa3 ba bordoColAlt">
          <h4 className="mt0">{label} · {LINGUE[selezionata]}</h4>
          {statiCampo(campo)}
          <div className="mt2"><TestoFormattato campo={campo} testo={target} vuoto="Nessuna traduzione." /></div>
        </div>
      </div>;
    })}
  </section>;
}
